import React, { useCallback, useMemo } from 'react';
import styles from './EditLessonContent.module.scss';
import { TextInput, TextInputValue } from '@components/text-input/TextInput';
import ImageUploadForm from '../edit-content/image-form/ImageUploadForm';
import { uploadFile } from '@services/File';
import AnswersForm from '../edit-content/feedback-form-editor/form-component/form-answers/AnswersForm';
import { LessonInput } from './text-input/LessonInput'
import { DurationInput } from './text-input/DurationInput'
import Checkbox from '@components/checkbox/Checkbox';
import Select from '@components/select/Select';
import { Lesson, SelectOption } from '@types';
import { HeaLessonFragment, ContentFragment, HeaPresentationModuleFragment } from '@src/api/graphql-codegen';
import { MultiLineTextInput } from '@src/components/text-input/MultiLineTextInput';
import { kebabCase } from 'change-case';
import { useAWSKeys } from '@src/hooks';

const validationErrors = {
  name: '',
  uniqueName: ''
};

export type ValidationErrorKeys = keyof typeof validationErrors;

export interface EditLessonType extends Omit<(Lesson & HeaLessonFragment), 'id' | '__typename' | 'originalLessonId' | 'modules' > {
  id?: string;
  originalLessonId?: string;
  modules?: ContentFragment[] | HeaPresentationModuleFragment[] | null;
  __typename?: 'Lesson' | 'HEALesson'
}

type Props = {
  lesson: EditLessonType;
  onChange: (p: Partial<EditLessonType>) => void;
  setErrors: (e: Partial<Record<ValidationErrorKeys, any>>) => void;
  errors: Partial<Record<ValidationErrorKeys, any>>;
  groups: SelectOption[];
  isHomeEd?: boolean;
  unitName?: string;
};

function LessonTabContent(props: Props) {
  const { lesson, errors, groups, onChange, setErrors, isHomeEd, unitName } = props

  const answers = useMemo(() => JSON.parse(lesson?.descriptionAnswers || '[]'), [lesson?.descriptionAnswers]);

  const group = useMemo(() => lesson?.group, [lesson?.group]);

  const { awsKeys } = useAWSKeys();

  const groupOptions = useMemo(() => isHomeEd ? [{label: 'All', value: 'all'}, {value: '4', label: 'Faith Program'}] : [{label: 'All', value: 'all'}, ...groups], [groups, isHomeEd]);

  const handleChangeText = useCallback((key: keyof typeof validationErrors) => (value: TextInputValue) => {
    setErrors({
      ...errors,
      [key]: '',
    });
    if (key === 'uniqueName') {
      value = value.toString().replace(/\s+/g, '-').replace(/[^A-Za-z-0-9-]/g, '').toLowerCase();
    }
    onChange({[key]: value});
  }, [errors, onChange, setErrors]);

  const handleChangeDuration = useCallback((value: TextInputValue) => {
    onChange({duration:  Number(value.toString())});
  }, [onChange]);

  const handleChangeImage = useCallback(async (image: string, file: File | undefined) => {
    if (file) {
      const location = await uploadFile(awsKeys, file, 'images');
      onChange({ image: location });
    }
  }, [onChange]);

  const handleStudentsRemoveOption = useCallback((id: number) => {
    const curr = [...answers];
    onChange({ descriptionAnswers: JSON.stringify(curr.filter((child, index) => {
      return index !== id
    })) });
  }, [answers, onChange]);

  const handleStudentsChangeOption = useCallback((index: number, value: TextInputValue) => {
    const curr = [...answers];
    curr[index] = value.toString()
    onChange({ descriptionAnswers: JSON.stringify(curr) });
  }, [answers, onChange]);

  const handleAddStudensOption = useCallback(() => {
    const curr = [...answers];
    curr.push('');
    onChange({ descriptionAnswers: JSON.stringify(curr) });
  }, [answers, onChange]);

  const handleChangeGroup = useCallback((v) => {
    onChange({ group: (!v?.value || `${v.value}` === 'all') ? null : v.value });
  }, [onChange]);

  const handleChangeDescriptionHeading = useCallback((value: TextInputValue) => {
    onChange({ descriptionHeading: value as string });
  }, [onChange]);

  const handleChangePrerequisite = useCallback((v: boolean) => {
    onChange({ prerequisite: v });
  }, [onChange]);

  const handleChangeSensitiveLesson = useCallback((v: boolean) => {
    onChange({ sensitive: v });
  }, [onChange]);

  const handleChangeSensitiveTooltip = useCallback((value: TextInputValue) => {
    onChange({ sensitiveTooltip: value.toString() });
  }, [onChange]);

  const handleActivate = useCallback((v: boolean) => {
    onChange({ alternativeActivated: v });
  }, [onChange]);

  const duration = useMemo(() => lesson?.duration ? lesson?.duration > 0 ? lesson?.duration : "" : "", [lesson?.duration]);
  return (
    <>
      <div className={styles.row}>
        <LessonInput name={`Lesson ${lesson?.number ?? 1}`} hasError={!!errors.name} subTitle={errors.name} label={'Lesson Name'} id={'name'} value={lesson?.name} onChange={handleChangeText('name')} />
      </div>
      <div className={styles.row} style={{ marginTop: '15px'}}>
        <TextInput hasError={!!errors?.uniqueName} subTitle={errors?.uniqueName} label={() => <div>Pretty URL <span style={{color: 'gray'}}>(If left empty, defaults to Lesson Name)</span></div>}
          id={'uniqueName'} placeholder={'Add pretty URL'} value={lesson?.uniqueName || ''} onChange={handleChangeText('uniqueName')}/>
        <div>
          <span style={{color: 'gray'}}>{`Sample: ${window.location.protocol}//${window.location.host}/${kebabCase(unitName || '')}/`}</span>
          <span>{kebabCase(lesson?.uniqueName || lesson.name)}</span>
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.descTitle}>List Description</div>
        <AnswersForm
          description={lesson?.descriptionHeading ?? 'This lesson helps students:'}
          answers={answers}
          showError={false}
          onRemove={handleStudentsRemoveOption}
          onChange={handleStudentsChangeOption}
          onAdd={handleAddStudensOption}
          onUpdateHeading={handleChangeDescriptionHeading}
        />
      </div>
      <div className={styles.row}>
        <DurationInput hasError={false} subTitle={''} type={'number'} label={'Duration'} id={'Duration'} value={duration} onChange={handleChangeDuration} />
      </div>
      <div className={styles.row}>
        <Select label={'Group'} options={groupOptions} value={group || 'all'} onChange={handleChangeGroup}/>
      </div>
      <div className={styles.row}>
        <ImageUploadForm url={lesson?.image || ''} onChange={handleChangeImage}/>
      </div>
      <div className={styles.smallRow}>
        <Checkbox checked={!!lesson?.prerequisite} onChange={handleChangePrerequisite} label={'This Lesson has no pre-requisites'}/>
      </div>
      <div className={styles.smallRow}>
        <Checkbox checked={!!lesson?.sensitive} onChange={handleChangeSensitiveLesson} label={'This Lesson contains Sensitive Content'}/>
        {lesson?.sensitive &&
        <MultiLineTextInput id='sensitiveUnit' allowSpecialCharacters={false} hasError={false} subTitle={''} label={''} value={lesson?.sensitiveTooltip ?? 'This Lesson contains sensitive content. We recommend previewing the lessons to ensure you are comfortable with material.'} onChange={handleChangeSensitiveTooltip} />}
      </div>
      <div className={styles.smallRow}>
        <Checkbox checked={!!lesson?.alternativeActivated} onChange={handleActivate} label={'Activate alternative lesson'} disabled={!lesson.sections?.length}/>
      </div>
    </>
  );
}

export default LessonTabContent;
